import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import en from './en.json';
import hi from './hi.json';
import { setRequestLocale } from '@/api/locale';

export type Locale = 'en' | 'hi';
const STORAGE_KEY = 'nurture.locale';
const tables: Record<Locale, Record<string, string>> = { en, hi: hi as Record<string, string> };
const warned = new Set<string>();

export const translate = (locale: Locale, key: string, vars?: Record<string, string | number>): string => {
  const raw = tables[locale][key] ?? tables.en[key];
  if (raw === undefined) {
    if (import.meta.env.DEV && !warned.has(key)) { warned.add(key); console.warn(`[i18n] missing key ${key}`); }
    return key;
  }
  return vars ? raw.replace(/\{([a-z]+)\}/g, (_, name: string) => String(vars[name] ?? `{${name}}`)) : raw;
};

const readStored = (): Locale | undefined => {
  try { const v = localStorage.getItem(STORAGE_KEY); return v === 'hi' || v === 'en' ? v : undefined; } catch { return undefined; }
};
export const readStoredLocale = readStored;

interface Ctx { locale: Locale; setLocale: (l: Locale) => void; t: (key: string, vars?: Record<string, string | number>) => string }
const LocaleContext = createContext<Ctx | undefined>(undefined);

export const LocaleProvider = ({ children, initial }: { children: ReactNode; initial?: Locale }) => {
  const [locale, setLocaleState] = useState<Locale>(initial ?? readStored() ?? 'en');
  const setLocale = useCallback((l: Locale) => { setLocaleState(l); try { localStorage.setItem(STORAGE_KEY, l); } catch { /* private mode */ } }, []);
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);
  // Set during render, not in an effect, so the first requests already carry the language.
  setRequestLocale(locale);
  const value = useMemo<Ctx>(() => ({ locale, setLocale, t: (k, v) => translate(locale, k, v) }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

const useCtx = (): Ctx => { const c = useContext(LocaleContext); if (!c) throw new Error('LocaleProvider missing'); return c; };
export const useLocale = () => { const { locale, setLocale } = useCtx(); return { locale, setLocale }; };
export const useT = () => useCtx().t;
